// Left panel: the app sidebar, on the Hopscotch design system.

import { X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth0 } from '@auth0/auth0-react'
import { useGlobalState } from '@/stores/globalState'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { LearnerSwitcher } from '@/components/hs/LearnerSwitcher'
import { Grain } from '@/components/hs/Grain'
import { beginLogout } from '@/lib/logoutLatch'
import type { AppModule } from '@/types'
import './styles/LeftPanel.css'

const NAV_ICONS = {
  home: (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 9l7-6 7 6M5 8v9h10V8" />
    </svg>
  ),
  user: (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <circle cx="10" cy="6.5" r="3.2" />
      <path d="M4 17c0-3.3 2.7-5 6-5s6 1.7 6 5" />
    </svg>
  ),
  book: (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 5C8 3.5 5 3.5 3 4v12c2-.5 5-.5 7 1 2-1.5 5-1.5 7-1V4c-2-.5-5-.5-7 1zM10 5v13" />
    </svg>
  ),
  journey: (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 16c3-7 5-7 7-3s4 4 7-3" strokeDasharray="0.5 4" />
      <circle cx="3" cy="16" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="17" cy="10" r="1.6" fill="currentColor" stroke="none" />
    </svg>
  ),
  gear: (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      <circle cx="10" cy="10" r="2.6" />
      <path d="M10 1.5v3M10 15.5v3M3 10H0M20 10h-3M4.5 4.5l2 2M13.5 13.5l2 2M15.5 4.5l-2 2M6.5 13.5l-2 2" strokeLinecap="round" />
    </svg>
  ),
  logout: (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 17H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4M13 14l4-4-4-4M17 10H7" />
    </svg>
  ),
} as const

const NAV_ITEMS: Array<{
  module: AppModule
  label: string
  icon: keyof typeof NAV_ICONS
  disabled?: boolean
}> = [
  { module: 'MY_GUIDES', label: 'Home', icon: 'home' },
  { module: 'EVALUATION', label: 'Profile', icon: 'user' },
  { module: 'BOOKS', label: 'Books', icon: 'book' },
  { module: 'LEARNING_JOURNEY', label: 'Learning Journey', icon: 'journey', disabled: true },
]

function NavItem({
  label,
  icon,
  active,
  disabled,
  onClick,
}: {
  label: string
  icon: keyof typeof NAV_ICONS
  active: boolean
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-current={active ? 'page' : undefined}
      className={[
        'left-panel__nav-item',
        active ? 'is-active' : '',
        disabled ? 'is-disabled' : '',
      ].filter(Boolean).join(' ')}
    >
      <span className="left-panel__nav-icon">{NAV_ICONS[icon]}</span>
      <span className="left-panel__nav-label">{label}</span>
      {disabled && <span className="left-panel__soon-tag">Coming soon</span>}
    </button>
  )
}

function LogoMark() {
  return (
    <div className="left-panel__logo">
      <span className="left-panel__logo-mark" />
      <span className="left-panel__logo-text">Hopscotch</span>
    </div>
  )
}

function AccountCluster() {
  const zustandLogout = useGlobalState((s) => s.logout)
  const { logout: auth0Logout } = useAuth0()

  const handleLogout = () => {
    beginLogout()
    auth0Logout({ logoutParams: { returnTo: window.location.origin } })
    zustandLogout()
  }

  return (
    <div className="hs-rule-dashed left-panel__account-cluster">
      <NavItem label="Account" icon="user" active={false} disabled onClick={() => {}} />
      <NavItem label="Settings" icon="gear" active={false} disabled onClick={() => {}} />
      <NavItem label="Log out" icon="logout" active={false} onClick={handleLogout} />
    </div>
  )
}

function PanelContent({ showLogo = true }: { showLogo?: boolean }) {
  const activeModule = useGlobalState((s) => s.activeModule)
  const setActiveModule = useGlobalState((s) => s.setActiveModule)

  return (
    <div className="hs-brand left-panel__content">
      <Grain />
      <div className="left-panel__content-inner">
        {showLogo && (
          <div className="left-panel__logo-wrap">
            <LogoMark />
          </div>
        )}

        <div className="left-panel__learner-switcher">
          <LearnerSwitcher />
        </div>

        <nav className="left-panel__nav">
          {NAV_ITEMS.map((item) => (
            <NavItem
              key={item.module}
              label={item.label}
              icon={item.icon}
              active={activeModule === item.module}
              disabled={item.disabled}
              onClick={() => {
                if (!item.disabled) setActiveModule(item.module)
              }}
            />
          ))}
        </nav>

        <div className="left-panel__account-wrap">
          <AccountCluster />
        </div>
      </div>
    </div>
  )
}

function Backdrop({ onClick }: { onClick: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-40 bg-black/50"
      onClick={onClick}
      aria-hidden="true"
    />
  )
}

export function LeftPanel() {
  const isDesktop = useIsDesktop()
  const leftPanelOpen = useGlobalState((s) => s.leftPanelOpen)
  const setLeftPanelOpen = useGlobalState((s) => s.setLeftPanelOpen)

  if (isDesktop) {
    return (
      <aside className="hs-brand left-panel left-panel--desktop">
        <PanelContent />
      </aside>
    )
  }

  return (
    <AnimatePresence>
      {leftPanelOpen && (
        <>
          <Backdrop onClick={() => setLeftPanelOpen(false)} />
          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="hs-brand fixed inset-y-0 left-0 z-50 flex w-[280px] flex-col shadow-xl"
          >
            <div className="left-panel__mobile-header">
              <LogoMark />
              <button
                type="button"
                onClick={() => setLeftPanelOpen(false)}
                className="left-panel__close-button"
                aria-label="Close navigation menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <PanelContent showLogo={false} />
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}
